/**
 * Nature juridique / statut de l'entité — désambiguïse SYSCOHADA vs SYCEBNL pour les
 * sociétés en zone OHADA. Avant ce module, SYCEBNL n'était jamais présélectionné
 * automatiquement (choix 100% manuel) car rien ne distinguait une société commerciale
 * d'une association/ONG/fondation — cf. companies.entity_type (migration
 * 20260705140000_add_company_entity_type.sql).
 *
 * Ce module ne dérive QUE l'ambiguïté SYSCOHADA/SYCEBNL. PCG/IFRS/SCF restent dérivés
 * du pays seul (la France, l'Algérie, etc. n'ont pas de norme SYCEBNL distincte) — à
 * n'appeler qu'après avoir déterminé que le pays relève de l'espace OHADA.
 */

export type EntityType =
  | 'association_ong'
  | 'fondation'
  | 'organisation_religieuse'
  | 'ordre_professionnel'
  | 'projet_developpement'
  | 'autre_ebnl'
  | 'societe_commerciale'
  | 'autre';

/** Pays membres OHADA — seule zone où l'ambiguïté SYSCOHADA/SYCEBNL existe. */
export const OHADA_COUNTRIES = new Set([
  'BJ',
  'BF',
  'CM',
  'CF',
  'KM',
  'CG',
  'CI',
  'GA',
  'GN',
  'GW',
  'GQ',
  'ML',
  'NE',
  'CD',
  'SN',
  'TD',
  'TG',
]);

export const ENTITY_TYPE_VALUES: EntityType[] = [
  'association_ong',
  'fondation',
  'organisation_religieuse',
  'ordre_professionnel',
  'projet_developpement',
  'autre_ebnl',
  'societe_commerciale',
  'autre',
];

/** Clé i18n (namespace entityTypes.*) pour chaque valeur de nature juridique. */
export const ENTITY_TYPE_I18N_KEY: Record<EntityType, string> = {
  association_ong: 'associationOng',
  fondation: 'fondation',
  organisation_religieuse: 'organisationReligieuse',
  ordre_professionnel: 'ordreProfessionnel',
  projet_developpement: 'projetDeveloppement',
  autre_ebnl: 'autreEbnl',
  societe_commerciale: 'societeCommerciale',
  autre: 'autre',
};

/** Formes juridiques à but non lucratif éligibles au SYCEBNL. */
export const SYCEBNL_ELIGIBLE_ENTITY_TYPES: EntityType[] = [
  'association_ong',
  'fondation',
  'organisation_religieuse',
  'ordre_professionnel',
  'projet_developpement',
  'autre_ebnl',
];

/**
 * Dérive SYSCOHADA vs SYCEBNL à partir de la nature juridique, pour une société déjà
 * identifiée comme relevant de l'espace OHADA. isNonProfit est une confirmation
 * explicite distincte de entityType (cas limites, ex. "Autre") ; si omise, elle est
 * déduite de la liste des formes éligibles.
 */
export function deriveOhadaAccountingStandard(
  entityType: EntityType | string | null | undefined,
  isNonProfit?: boolean
): 'SYCEBNL' | 'SYSCOHADA' {
  const eligible = !!entityType && SYCEBNL_ELIGIBLE_ENTITY_TYPES.includes(entityType as EntityType);
  const confirmed = isNonProfit ?? eligible;
  return eligible && confirmed ? 'SYCEBNL' : 'SYSCOHADA';
}
